import { Button } from '@1trading/react/components/ui/button';
import { Card, CardContent } from '@1trading/react/components/ui/card';
import { GradientIcon } from '@1trading/react/components/ui/gradient-icon';
import { useIsMutating } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Timer } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { LdFeatureFlags } from '@/config/features';
import { useInstitutionOnboardingFuturesNext } from '@/features/institution/hooks/useInstitutionOnboardingFuturesNext';
import {
  useProfileInstitutionDetailsAnswers,
  useProfileInstitutionDetailsDraft,
  useProfileInstitutionDetailsV2Answers,
  useProfileInstitutionDetailsVersion,
} from '@/hooks/queries/use-profile-institution-details';
import { useProfileVerificationInformation, useProfileVerificationSubmissionWait } from '@/hooks/queries/use-profile-verification-information';
import { QUERY_KEY as PUT_USER_QUERY_KEY, useUserPutMutation } from '@/hooks/queries/use-user';
import { useOnboardingCompleteRedirect } from '@/hooks/use-onboarding-complete-redirect';
import BaseLayout from '@/layouts/base';
import LoadingPage from '@/pages/loading';
import { INSTITUTION_DETAILS_VERSION } from '@/services/api/profile';

const ID = 'institution-onboarding-complete-page';

type InstitutionOnboardingCompletePageFuturesAction = 'futuresSignup' | 'futuresContinue';

const useInstitutionOnboardingCompletePageFuturesAction = () => {
  const { data: version } = useProfileInstitutionDetailsVersion();
  const { data: institutionDetailsV2Answers } = useProfileInstitutionDetailsV2Answers();
  const { isLoading, route: futuresNextRoute } = useInstitutionOnboardingFuturesNext();
  const { futuresEnabled, futuresPublicTrading } = useFlags<LdFeatureFlags>();
  const isFuturesEnabledWithPublicTradingEnabled = futuresEnabled && futuresPublicTrading.enabled;
  // A V2 firm that selected futures in the application is only offered the futures steps left over
  const isFuturesSignupApplicable = version === INSTITUTION_DETAILS_VERSION.V1 || institutionDetailsV2Answers?.products?.futures !== 'YES';
  const action: InstitutionOnboardingCompletePageFuturesAction | undefined = isFuturesSignupApplicable ? 'futuresSignup' : futuresNextRoute ? 'futuresContinue' : undefined;

  return { isLoading, action: isFuturesEnabledWithPublicTradingEnabled ? action : undefined };
};

const InstitutionOnboardingCompletePageFuturesButton = ({ action }: { action: InstitutionOnboardingCompletePageFuturesAction }) => {
  const { t } = useTranslation();

  return (
    <Button size="lg" variant="primary" asChild>
      <Link to="/institution/futures" replace>
        {action === 'futuresContinue' ? t('institutionDetails.futuresOnboarding.continueFutures') : t('onboardingProgress.complete.nextSteps.futuresSignup.title')}
      </Link>
    </Button>
  );
};
InstitutionOnboardingCompletePageFuturesButton.displayName = 'InstitutionOnboardingCompletePageFuturesButton';

// A complete record on an older version stays valid, so the update is offered rather than required
const useInstitutionOnboardingCompletePageUpdate = () => {
  const { data: version } = useProfileInstitutionDetailsVersion();
  const isOnOlderVersion = version === INSTITUTION_DETAILS_VERSION.V1;
  const { isLoading, data: draft } = useProfileInstitutionDetailsDraft(isOnOlderVersion);

  return { isLoading, isUpdateApplicable: isOnOlderVersion && !!draft };
};

const InstitutionOnboardingCompletePageUpdateNotice = () => {
  const { t } = useTranslation();

  return (
    <Card className="shadow-none" bordered>
      <CardContent className="flex flex-col gap-4 pt-6">
        <div className="space-y-1">
          <h2 className="font-semibold">{t('institutionDetails.update.notice.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('institutionDetails.update.notice.description')}</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/institution/onboarding/update" replace>
            {t('institutionDetails.update.notice.action')}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};
InstitutionOnboardingCompletePageUpdateNotice.displayName = 'InstitutionOnboardingCompletePageUpdateNotice';

const InstitutionOnboardingCompletePage = () => {
  const { isCompleteOnboarding: isCompleteInstitutionDetailsOnboarding, isLoading: isLoadingInstitutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation } = useProfileVerificationInformation();
  const isCompleteVerificationInformation = !!verificationInformation?.verification && verificationInformation.verification.status !== 'NOT_VERIFIED';
  const isReadyToPutUser = !!isCompleteInstitutionDetailsOnboarding && isCompleteVerificationInformation;
  const { isWaiting: isWaitingForVerification } = useProfileVerificationSubmissionWait(isReadyToPutUser);
  const { isLoading: isLoadingFuturesAction, action: futuresAction } = useInstitutionOnboardingCompletePageFuturesAction();
  const { isLoading: isLoadingUpdate, isUpdateApplicable } = useInstitutionOnboardingCompletePageUpdate();
  const { isIdle: isIdlePutUser, mutate: putUser } = useUserPutMutation();
  const isPendingPutUser = useIsMutating({ mutationKey: PUT_USER_QUERY_KEY }) > 0;
  const { t } = useTranslation();
  const hasTriggeredPutUser = useRef(false);
  const shouldPutUser = isReadyToPutUser && !isWaitingForVerification;
  const isPuttingUser = shouldPutUser && (isIdlePutUser || isPendingPutUser);
  const isLoading =
    isLoadingInstitutionDetailsAnswers || isLoadingVerificationInformation || isLoadingFuturesAction || isLoadingUpdate || isWaitingForVerification || isPuttingUser;

  useEffect(() => {
    if (shouldPutUser && !hasTriggeredPutUser.current) {
      hasTriggeredPutUser.current = true;

      putUser();
    }
  }, [putUser, shouldPutUser]);

  const { isRedirecting } = useOnboardingCompleteRedirect(isLoading, { followExternal: false });

  if (isLoading) return <LoadingPage />;

  if (isRedirecting)
    return (
      <LoadingPage>
        <p>{t('redirecting')}</p>
      </LoadingPage>
    );

  if (!isCompleteInstitutionDetailsOnboarding) {
    return <Navigate to="/institution/onboarding" replace />;
  }

  if (!isCompleteVerificationInformation) {
    return <Navigate to="/institution/onboarding/verification" replace />;
  }

  return (
    <BaseLayout className="-md:justify-center space-y-8" id={ID} size="sm">
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <Timer />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>{t('institutionOnboardingProgress.complete.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionOnboardingProgress.complete.description')}</PageHeadingDescription>
        {futuresAction ? (
          <PageHeadingTitle className="!mt-6 text-lg md:text-2xl" asChild>
            <h2>{t('onboardingProgress.complete.nextSteps.title')}</h2>
          </PageHeadingTitle>
        ) : null}
      </PageHeading>
      <footer className="flex flex-col gap-4">
        {isUpdateApplicable ? <InstitutionOnboardingCompletePageUpdateNotice /> : null}
        {futuresAction ? <InstitutionOnboardingCompletePageFuturesButton action={futuresAction} /> : null}
        <Button size="lg" variant={futuresAction ? 'outline' : 'primary'} asChild>
          <a href={import.meta.env.VITE_APP_EXCHANGE_URL}>{t('buttons.exchange')}</a>
        </Button>
      </footer>
    </BaseLayout>
  );
};
InstitutionOnboardingCompletePage.displayName = 'InstitutionOnboardingCompletePage';

export default InstitutionOnboardingCompletePage;
